import React from 'react';
import { GraduationCap, Users, BookOpen, HelpCircle } from 'lucide-react';

/**
 * Компонент раздела формы для параметров вычета на обучение
 */
const EducationDeductionSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  educationTypes 
}) => {
  return (
    <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <GraduationCap className="w-5 h-5 mr-2 text-yellow-600" />
        Параметры вычета на обучение
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Расходы на обучение */}
        <div>
          <label htmlFor="educationExpenses" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Расходы на обучение
          </label>
          <input
            type="text"
            id="educationExpenses"
            name="educationExpenses"
            value={formData.educationExpenses}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.educationExpenses ? 'input-error' : ''}`}
            placeholder="Введите сумму расходов"
          />
          {errors.educationExpenses && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.educationExpenses}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Для собственного обучения лимит: 120 000 ₽, для детей: 50 000 ₽ на каждого ребенка
          </p>
        </div>

        {/* Тип обучения */}
        <div>
          <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип обучения
          </legend>
          <div className="space-y-2">
            {educationTypes.map(type => (
              <label key={type.id} className="flex items-center">
                <input
                  type="radio"
                  name="educationType"
                  value={type.id}
                  checked={formData.educationType === type.id}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {type.name}
                  {type.id === 'children' && (
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                      До 24 лет
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
          {errors.educationType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.educationType}
            </p>
          )}
        </div>

        {/* Блок для обучения детей */}
        {formData.educationType === 'children' && (
          <>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="hasChildren"
                  name="hasChildren"
                  type="checkbox"
                  checked={formData.hasChildren}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="hasChildren" className="font-medium text-gray-700 dark:text-gray-300">
                  Есть дети на очном обучении
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Дети до 24 лет на очной форме обучения
                </p>
              </div>
            </div>

            {formData.hasChildren && (
              <div>
                <label htmlFor="childrenCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <Users className="w-4 h-4 mr-2 text-yellow-600" />
                  Количество детей на обучении
                </label>
                <select
                  id="childrenCount"
                  name="childrenCount"
                  value={formData.childrenCount}
                  onChange={handleInputChange}
                  className={`form-select w-full ${errors.childrenCount ? 'input-error' : ''}`}
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num.toString()}>
                      {num} {num === 1 ? 'ребенок' : num < 5 ? 'ребенка' : 'детей'}
                    </option>
                  ))}
                </select>
                {errors.childrenCount && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.childrenCount}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Максимальный вычет: {(parseInt(formData.childrenCount) || 1) * 50000} ₽ 
                  (возврат: {((parseInt(formData.childrenCount) || 1) * 50000 * 0.13).toLocaleString('ru-RU')} ₽)
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Информационный блок */}
      <div className="mt-4 flex items-start p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
        <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
            Важно знать о вычете на обучение
          </h4>
          <div className="text-xs text-blue-600 dark:text-blue-400">
            <p className="mb-1">
              Вычет на собственное обучение предоставляется за любую форму обучения (очную, заочную, вечернюю) в лицензированных образовательных учреждениях.
            </p>
            <p className="mb-1">
              <strong>Вычет за обучение детей</strong> можно получить только при очной форме обучения детей до 24 лет. Лимит: 50 000 ₽ на каждого ребенка в год.
            </p>
            <p>
              Вычет также распространяется на обучение братьев и сестер до 24 лет, но только при очной форме обучения.
            </p>
          </div>
        </div>
      </div>

      {/* Перечень документов */}
      <div className="mt-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <BookOpen className="w-4 h-4 mr-2 text-yellow-600" />
          Необходимые документы:
        </h4>
        <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc pl-5 space-y-1">
          <li>Договор с образовательным учреждением</li>
          <li>Лицензия образовательного учреждения</li>
          <li>Платежные документы (квитанции, чеки)</li>
          <li>Справка об очной форме обучения (для вычета за детей)</li>
          <li>Свидетельство о рождении ребенка или документ о родстве</li>
          <li>Документы, подтверждающие оплату обучения именно родителем (если платил за ребенка)</li>
          <li>Если оплата проводилась из средств материнского капитала, вычет не предоставляется</li>
        </ul>
      </div>
    </div>
  );
};

export default EducationDeductionSection;